// src/components/agent/parts/ThinkingIndicator.js
//
// The dots + expandable thinking text, extracted from MessageDisplay.js
// (frontend spec §8.6), restyled for the page surface, plus the compacting
// and plain "working" variants (§6.2's headless-turn indicator).
import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const ThinkingDot = styled('span')({
  width: 4,
  height: 4,
  borderRadius: '50%',
  backgroundColor: 'currentColor',
  animation: 'dotPulse 1.4s ease-in-out infinite',
  '@keyframes dotPulse': {
    '0%, 60%, 100%': { opacity: 0.3 },
    '30%': { opacity: 1 },
  },
});

const PulsingIcon = styled(AutoAwesomeIcon)({
  fontSize: 16,
  animation: 'iconPulse 2s ease-in-out infinite',
  '@keyframes iconPulse': {
    '0%, 100%': { opacity: 0.6 },
    '50%': { opacity: 1 },
  },
});

const VARIANT_LABELS = {
  thinking: 'Thinking',
  compacting: 'Compacting conversation',
  working: 'Working',
};

function ThinkingIndicator({ variant = 'thinking', thinkingText }) {
  const [expanded, setExpanded] = useState(false);
  const hasText = variant === 'thinking' && !!thinkingText;

  return (
    <Box sx={{ py: 0.5, color: 'text.secondary' }}>
      <Box
        display="flex"
        alignItems="center"
        gap={0.75}
        onClick={() => hasText && setExpanded((prev) => !prev)}
        sx={{ cursor: hasText ? 'pointer' : 'default', userSelect: 'none' }}
      >
        <PulsingIcon />
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'inherit' }}>
          {VARIANT_LABELS[variant] || VARIANT_LABELS.thinking}
        </Typography>
        <Box display="flex" gap="3px" alignItems="center">
          <ThinkingDot sx={{ animationDelay: '0s' }} />
          <ThinkingDot sx={{ animationDelay: '0.2s' }} />
          <ThinkingDot sx={{ animationDelay: '0.4s' }} />
        </Box>
        {hasText && (
          expanded
            ? <ExpandLessIcon sx={{ fontSize: 16, ml: 0.5 }} />
            : <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.5 }} />
        )}
      </Box>
      {expanded && hasText && (
        <Typography
          variant="body2"
          sx={{
            mt: 0.75,
            pl: 3.25,
            color: 'text.disabled',
            fontStyle: 'italic',
            fontSize: '0.8rem',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {thinkingText}
        </Typography>
      )}
    </Box>
  );
}

export default ThinkingIndicator;
